import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FCC } from "types";

import { Sim } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import {
  SubscriptionInfoBox,
  SubscriptionInfoRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import {
  DiscountChip,
  PriceRow,
} from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/SubscriptionPrice";
import { useSubscriptionPriceBreakdown } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/useSubscriptionPriceBreakdown";
import { useAlert } from "@/hooks/useAlert";
import { useSetAutoRenew } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import {
  getExtendedFlatNameFromLocalFlatSpeed,
  hasDifferentSuccessor,
} from "@/utils/deviceUtils";
import { getNextPaymentDate } from "@/utils/tariffUtils";
import { getCurrency } from "@/utils/translationHelpers";

export interface SubscriptionAutoRenewalProps {
  /**
   * Rendered inline (desktop) instead of as a full page (mobile): closing or
   * confirming should return to the status card in place, not navigate away.
   * Omitted when mounted as the route's own element (the mobile case).
   */
  onClose?: () => void;
}

export const SubscriptionAutoRenewal: FCC<SubscriptionAutoRenewalProps> = ({
  onClose,
}) => {
  const { t: tPrefixed } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.autoRenewal",
  });
  const { t, i18n } = useTranslation("portal");
  const { t: tDefault } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showAlert = useAlert();

  const { data: simDetail, isLoading } = useDisplayedSim();

  const close = () =>
    onClose
      ? onClose()
      : navigate(`../display?simId=${simDetail?.simId}`, { replace: true });

  const { mutate: sendSetAutoRenew, isLoading: isLoadingSetAutoRenew } =
    useSetAutoRenew({
      mutation: {
        onSuccess: () => {
          // The mutation itself already succeeded server-side by this point;
          // a rejected refetch must still close the panel and confirm it,
          // not leave the customer staring at a stale, already-submitted form.
          // Returned so the mutation stays pending until invalidation and
          // the close/alert both complete.
          return queryClient.invalidateQueries(["/sim/sim"]).finally(() => {
            close();
            showAlert({
              type: AlertTypes.success,
              text: tPrefixed(
                autoRenewalActive
                  ? "disable.successMessage"
                  : "enable.successMessage",
              ),
            });
          });
        },
      },
    });

  const changeAutoRenew = (newValue: boolean) => {
    if (simDetail?.simId) {
      sendSetAutoRenew({
        simId: simDetail?.simId,
        params: { autoRenew: newValue },
      });
    }
  };

  const autoRenewalActive = !!simDetail?.autoRenew;

  // Where the very next bill lands once auto-renewal kicks back in.
  // `getNextPaymentDate` counts forward from today, so without
  // `remainingDays` it lands on today and the row would promise a bill
  // that isn't due - say nothing about the date rather than the wrong
  // thing (same guard as SubscriptionStatus's nextBillDate).
  const nextBillDate =
    simDetail?.remainingDays !== undefined
      ? getLocalizedDateString(
          getNextPaymentDate(simDetail.remainingDays),
          i18n.language,
        )
      : undefined;
  // Only the "enable" screen shows the price breakdown - skip the discount
  // and add-on computation on the "disable/pause" screen where it's unused.
  const priceBreakdown = useSubscriptionPriceBreakdown(
    autoRenewalActive ? undefined : simDetail,
  );

  return !isLoading && simDetail ? (
    <SubscriptionCard
      dataTestid="subscription-auto-renewal"
      stickyHeader
      title={tPrefixed(autoRenewalActive ? "disable.title" : "enable.title")}
      action={
        <>
          <Button
            dataTestid="cancel-button"
            size="sm"
            variant="secondary"
            onClick={close}
          >
            {tPrefixed("buttons.cancel")}
          </Button>
          <Button
            dataTestid="success-button"
            size="sm"
            variant="primary"
            disabled={isLoadingSetAutoRenew}
            onClick={() => changeAutoRenew(!autoRenewalActive)}
          >
            {tPrefixed("buttons.confirm")}
          </Button>
        </>
      }
    >
      {autoRenewalActive ? (
        <>
          <p className="text-body-sm text-foreground-secondary">
            <Trans
              t={tPrefixed}
              values={{ count: simDetail.remainingDays ?? 0 }}
            >
              disable.description
            </Trans>
          </p>

          {hasDifferentSuccessor(simDetail) && (
            <p className="text-body-sm text-foreground-secondary">
              <Trans
                t={t}
                values={{
                  flatName: getExtendedFlatNameFromLocalFlatSpeed(
                    simDetail?.successorForRenew?.description,
                  ),
                }}
                i18nKey="cockpit.managementTile.details.sections.autoRenewal.disable.successorWarning"
              />
            </p>
          )}

          <SubscriptionInfoBox>
            <SubscriptionInfoRow
              icon={<Sim className="size-6" />}
              title={getExtendedFlatNameFromLocalFlatSpeed(
                simDetail.localFlatSpeed,
              )}
            />
          </SubscriptionInfoBox>

          <Notification
            title={tDefault("Content.goodToKnow")}
            description={tPrefixed("disable.goodToKnow")}
          />
        </>
      ) : (
        <>
          <p className="text-body-sm text-foreground-secondary">
            <Trans
              t={tPrefixed}
              values={{ days: simDetail?.minContractDuration }}
            >
              enable.description
            </Trans>
          </p>

          <SubscriptionInfoBox>
            <SubscriptionInfoRow
              icon={<Sim className="size-6" />}
              title={getExtendedFlatNameFromLocalFlatSpeed(
                simDetail.localFlatSpeed,
              )}
            />
          </SubscriptionInfoBox>

          <div className="flex w-full flex-col gap-2">
            <p className="text-body-sm font-semibold text-foreground-primary">
              {tPrefixed("enable.nextBillTitle")}
            </p>
            <p className="text-body-sm text-foreground-secondary">
              {tPrefixed("enable.nextBillDescription")}
            </p>
          </div>

          <div className="flex w-full flex-col">
            <PriceRow
              withBackdrop
              variant="strong"
              label={priceBreakdown.productName}
              value={getCurrency(priceBreakdown.basePrice, "CHF")}
            />

            {priceBreakdown.discounts.map(
              ({ id, amount, campaignPeriod, discountName }, index) => {
                const period = priceBreakdown.getDiscountPeriod(
                  campaignPeriod!,
                );

                return (
                  <PriceRow
                    key={id}
                    withDivider={
                      priceBreakdown.addOns.length === 0 &&
                      index === priceBreakdown.discounts.length - 1
                    }
                    label={
                      <>
                        {discountName}
                        {period && (
                          <>
                            <br />
                            {period}
                          </>
                        )}
                      </>
                    }
                    value={<DiscountChip amount={amount ?? 0} />}
                  />
                );
              },
            )}

            {priceBreakdown.addOns.map((addOn, index) => (
              <PriceRow
                key={addOn.label}
                withDivider={index === priceBreakdown.addOns.length - 1}
                label={addOn.label}
                value={getCurrency(addOn.price, "CHF")}
              />
            ))}

            <PriceRow
              variant="strong"
              // Without a known bill date, say nothing rather than guess -
              // the interval in the value column still carries the total.
              label={
                nextBillDate
                  ? tPrefixed("enable.dueDate", { date: nextBillDate })
                  : ""
              }
              equalColumns
              value={
                <>
                  <span>{getCurrency(priceBreakdown.totalPrice, "CHF")}</span>
                  <span className="text-body-xs font-regular leading-md">
                    {t("cockpit.managementTile.tariff.payInterval", {
                      days: simDetail.minContractDuration ?? 30,
                    })}
                  </span>
                </>
              }
            />
          </div>

          <Notification
            title={tDefault("Content.goodToKnow")}
            description={tPrefixed("enable.goodToKnow")}
          />
        </>
      )}
    </SubscriptionCard>
  ) : (
    <BrandLoadingScreen />
  );
};
